import { useMemo, useState, type FormEvent } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import JsonLd from "@/components/feature/JsonLd";
import PageSeo from "@/components/feature/PageSeo";
import GossipCard from "@/components/feature/GossipCard";
import { categories, getItemsByCategory, gossipItems } from "@/mocks/gossip";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const q = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(q);

  const activeCategory = slug ? categories.find((c) => c.slug === slug) : undefined;

  const items = useMemo(() => {
    let list = slug ? getItemsByCategory(slug) : gossipItems;
    if (q.trim()) {
      const key = q.trim().toLowerCase();
      list = list.filter(
        (item) =>
          item.title.toLowerCase().includes(key) ||
          item.summary.toLowerCase().includes(key) ||
          item.tags.some((tag) => tag.toLowerCase().includes(key))
      );
    }
    return list;
  }, [slug, q]);

  const pageTitle = activeCategory ? activeCategory.name : "全部瓜料";
  const pageDesc = activeCategory
    ? activeCategory.desc
    : "汇集娱乐爆料、网红动态、影视综艺、电竞与社会热点的视觉画面，按分类与关键词自由浏览。";

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/category?q=${encodeURIComponent(value)}` : "/category");
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "https://hebeishifenhao.cn/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: pageTitle,
        item: activeCategory
          ? `https://hebeishifenhao.cn/category/${activeCategory.slug}`
          : "https://hebeishifenhao.cn/category",
      },
    ],
  };

  const collectionLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${pageTitle} - 瓜田前线`,
    description: pageDesc,
    inLanguage: "zh-CN",
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <PageSeo
        title={`${pageTitle} - 网曝吃瓜画面合集 | 瓜田前线`}
        description={`${pageDesc} 含网曝吃瓜视频、图片与实拍截图整理。`}
        keywords={`${pageTitle},网曝吃瓜,吃瓜视频,吃瓜图片,吃瓜截图`}
        canonical={
          activeCategory
            ? `https://hebeishifenhao.cn/category/${activeCategory.slug}`
            : "https://hebeishifenhao.cn/category"
        }
      />
      <JsonLd data={breadcrumbLd} />
      <JsonLd data={collectionLd} />
      <Navbar />

      <main className="flex-1 w-full">
        <header className="w-full bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-8 py-10 md:py-14">
            <nav aria-label="面包屑" className="flex items-center gap-2 text-xs text-foreground-500 mb-5">
              <Link to="/" className="hover:text-primary-600 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              {activeCategory ? (
                <>
                  <Link to="/category" className="hover:text-primary-600 transition-colors">
                    全部瓜料
                  </Link>
                  <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                  <span className="text-foreground-800">{activeCategory.name}</span>
                </>
              ) : (
                <span className="text-foreground-800">全部瓜料</span>
              )}
            </nav>

            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div className="max-w-2xl">
                <p className="inline-flex items-center gap-2 text-xs font-label tracking-[0.2em] uppercase text-primary-600 mb-3">
                  <span className="w-6 h-px bg-primary-500 inline-block"></span>
                  {activeCategory ? "Category" : "All Gossip"}
                </p>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
                  {pageTitle}
                </h1>
                <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">{pageDesc}</p>
              </div>

              <form onSubmit={handleSearch} className="w-full lg:w-80 shrink-0">
                <div className="flex items-center gap-2 px-4 rounded-md bg-background-50 border border-background-300 focus-within:border-primary-400">
                  <i className="ri-search-line text-foreground-500 text-base" aria-hidden="true"></i>
                  <label htmlFor="list-search" className="sr-only">
                    搜索瓜料
                  </label>
                  <input
                    id="list-search"
                    type="text"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="搜索瓜料关键词"
                    className="flex-1 min-w-0 bg-transparent border-0 text-sm py-3 text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                  />
                  <button type="submit" className="text-sm font-medium text-primary-600 whitespace-nowrap cursor-pointer">
                    搜索
                  </button>
                </div>
              </form>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-2.5">
              <Link
                to="/category"
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  !slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 border border-background-300 text-foreground-700 hover:border-primary-300"
                }`}
              >
                全部
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    slug === cat.slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-50 border border-background-300 text-foreground-700 hover:border-primary-300"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {q && (
              <p className="mt-5 text-sm text-foreground-600">
                关键词「<span className="text-primary-600 font-medium">{q}</span>」共找到 {items.length} 条结果
                <Link to={slug ? `/category/${slug}` : "/category"} className="ml-3 text-primary-600 hover:underline">
                  清除筛选
                </Link>
              </p>
            )}
          </div>
        </header>

        <section className="w-full px-4 md:px-8 py-10 md:py-14">
          {items.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {items.map((item) => (
                <GossipCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="w-full py-20 flex flex-col items-center text-center">
              <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center text-3xl text-foreground-500">
                <i className="ri-search-eye-line" aria-hidden="true"></i>
              </span>
              <h2 className="mt-5 font-heading text-lg font-semibold text-foreground-900">暂时没有匹配的瓜料</h2>
              <p className="mt-2 text-sm text-foreground-600">换个关键词试试，或者回到全部瓜料浏览。</p>
              <Link
                to="/category"
                className="mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
              >
                返回全部瓜料
              </Link>
            </div>
          )}
        </section>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
}